import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSnackbar } from 'notistack';
import * as api from 'src/backend';

const OnboardingRequestsContext = createContext();

export function OnboardingRequestsProvider({ children }) {
  const [cache, setCache] = useState(null);
  const [valid, setValid] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (valid) {
      return;
    }

    setLoading(true);
    api.team
      .getAllOnboardingRequests()
      .then(setCache)
      .catch((err) => {
        console.error(err);
        enqueueSnackbar('There was an error getting your onboarding requests', {
          variant: 'error',
        });
      })
      .finally(() => {
        setLoading(false);
        setValid(true);
      });
  }, [valid, enqueueSnackbar, setCache, setLoading, setValid]);

  const invalidate = useCallback(() => {
    setValid(false);
  }, []);

  return (
    <OnboardingRequestsContext.Provider
      value={{
        items: Array.isArray(cache) ? cache : [],
        isLoading,
        invalidate,
      }}
    >
      {children}
    </OnboardingRequestsContext.Provider>
  );
}

export function useOnboardingRequests() {
  const ctx = useContext(OnboardingRequestsContext);

  if (!ctx) {
    throw new Error(
      'Could not use an OnboardingRequestsContext. Did you forget an OnboardingRequestsProvider'
    );
  }

  return ctx;
}

export function useOnboardingRequestMissingCount() {
  const { items, isLoading, invalidate } = useOnboardingRequests();

  const incompleteCount = useMemo(
    () =>
      items.reduce(
        (sum, request) =>
          sum +
          (request.request_status === 'rejected'
            ? 0
            : request.request_status === 'pending'
              ? 1
              : Number(request.total_incomplete_items || 0)),
        0
      ),
    [items]
  );

  return {
    incompleteCount,
    isLoading,
    invalidate,
  };
}

export function useDonorOnboardingComplete() {
  const { items, isLoading, invalidate } = useOnboardingRequests();

  const complete = useMemo(() => {
    // If there are no onboarding requests, consider it complete
    if (!items || items.length === 0) {
      return true;
    }

    // Treat a request as satisfied if it's marked compliant OR completed_items >= needed_items.
    // If a request has no required items (needed_items === 0), it's inherently satisfied.
    return items.every((request) => {
      if (request?.request_status === 'pending') {
        return false;
      }
      const needed = Number(request?.needed_items || 0);
      const completed = Number(request?.completed_items || 0);
      const isMarkedCompliant = !!request?.is_marked_compliant;
      if (needed === 0) {
        return true;
      }
      return isMarkedCompliant || completed >= needed;
    });
  }, [items]);

  return {
    complete,
    isLoading,
    invalidate,
  };
}
